import React, { useEffect, useRef, useState } from 'react';
import Layout from '../components/Layout';
import { Mic, Square, Music2, Scissors, Sparkles, Key, ExternalLink, RefreshCw, Upload as UploadIcon, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { useNavigate } from 'react-router-dom';

const KeyGate = ({ service, children }) => {
  if (service?.enabled) return children;
  return (
    <div className="post-card p-5 mb-4 flex items-start gap-3" data-testid={`gate-${service?.provider || 'x'}`}>
      <div className="w-10 h-10 rounded-full bg-[#FFB000]/10 flex items-center justify-center flex-shrink-0">
        <Key size={16} className="text-[#FFB000]" />
      </div>
      <div className="flex-1">
        <div className="text-white font-heading font-bold text-sm">Add your {service?.provider} key to unlock</div>
        <div className="text-[#A1A1AA] text-xs mt-1 leading-relaxed">
          Grab a free key at
          {' '}
          <a href={service?.signup_url} target="_blank" rel="noreferrer" className="text-[#FFB000] hover:underline inline-flex items-center gap-1">
            {service?.signup_url?.replace(/^https?:\/\//, '')} <ExternalLink size={10} />
          </a>
          {' '}then drop it into <code className="text-[#FFB000]">backend/.env</code> as <code className="text-[#FFB000]">{service?.env_var}</code> and restart. This tile lights up automatically.
        </div>
      </div>
    </div>
  );
};

/**
 * ShazamTile — records ~8s from the mic and posts to /ai/shazam.
 */
const ShazamTile = ({ service }) => {
  const [state, setState] = useState('idle');   // idle | recording | matching | done
  const [result, setResult] = useState(null);
  const recRef = useRef(null);
  const chunksRef = useRef([]);

  const start = async () => {
    if (!service.enabled) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      chunksRef.current = [];
      const mr = new MediaRecorder(stream);
      mr.ondataavailable = e => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || 'audio/webm' });
        setState('matching');
        try {
          const form = new FormData();
          form.append('file', new File([blob], 'clip.webm', { type: blob.type }));
          const { data } = await api.post('/ai/shazam', form, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 45000 });
          setResult(data);
          setState('done');
        } catch (err) {
          toast.error(err.response?.data?.detail?.message || 'Match failed');
          setState('idle');
        }
      };
      mr.start();
      recRef.current = mr;
      setState('recording');
      // Auto-stop at 8s
      setTimeout(() => { if (mr.state !== 'inactive') mr.stop(); }, 8000);
    } catch {
      toast.error('Microphone permission denied.');
    }
  };

  return (
    <div className="post-card p-5" data-testid="shazam-tile">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-full bg-[#FFB000]/10 flex items-center justify-center"><Mic size={14} className="text-[#FFB000]" /></div>
        <div className="text-white font-heading font-black text-lg">Track ID</div>
      </div>
      <div className="text-[#A1A1AA] text-xs mb-4 leading-relaxed">
        Hold up your phone to any speaker — RAW listens for 8 seconds and tells you what's playing.
      </div>
      <KeyGate service={service}>
        <button
          onClick={start}
          disabled={state === 'recording' || state === 'matching' || !service.enabled}
          className={`btn-pill w-full flex items-center justify-center gap-2 text-sm disabled:opacity-40 ${state === 'recording' ? 'bg-[#EF4444] text-white' : 'btn-primary'}`}
          data-testid="shazam-btn"
        >
          {state === 'recording' && (<><Square size={14} /> Listening…</>)}
          {state === 'matching' && (<><RefreshCw size={14} className="animate-spin" /> Matching</>)}
          {(state === 'idle' || state === 'done') && (<><Mic size={14} /> Tap to identify</>)}
        </button>
        {state === 'done' && result && (
          <div className="mt-4 p-3 rounded-xl border border-[#FFB000]/30 bg-[#FFB000]/5" data-testid="shazam-result">
            {result.matched ? (
              <>
                <div className="text-[10px] uppercase tracking-widest text-[#FFB000] font-bold">Matched</div>
                <div className="text-white font-heading font-bold text-base">{result.title}</div>
                <div className="text-[#A1A1AA] text-xs">{result.artist} · {result.album}</div>
                {result.song_link && <a href={result.song_link} target="_blank" rel="noreferrer" className="mt-2 text-xs text-[#FFB000] hover:underline inline-flex items-center gap-1">Open on AudD <ExternalLink size={10} /></a>}
              </>
            ) : (
              <div className="text-[#A1A1AA] text-sm">No match — try louder or closer to the source.</div>
            )}
          </div>
        )}
      </KeyGate>
    </div>
  );
};

/**
 * SongGenTile — lyrics + genre + voice → Suno job → post to feed.
 */
const SongGenTile = ({ service }) => {
  const navigate = useNavigate();
  const [lyrics, setLyrics] = useState('');
  const [genre, setGenre] = useState('afrobeats');
  const [voice, setVoice] = useState('sung');
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!lyrics.trim()) { toast.error('Write some lyrics first'); return; }
    setBusy(true);
    try {
      const { data } = await api.post('/ai/song', { lyrics: lyrics.trim(), genre, voice });
      setJob(data);
      toast.success('Song queued with Suno — this can take a minute or two.');
    } catch (err) {
      toast.error(err.response?.data?.detail?.message || 'Could not queue the song');
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!job || job.status === 'ready') return;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/ai/song/${job.job_id}`);
        setJob(data);
        if (data.status === 'ready') clearInterval(t);
      } catch {}
    }, 5000);
    return () => clearInterval(t);
  }, [job]);

  return (
    <div className="post-card p-5 lg:col-span-2" data-testid="songgen-tile">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-full bg-[#FFB000]/10 flex items-center justify-center"><Music2 size={14} className="text-[#FFB000]" /></div>
        <div className="text-white font-heading font-black text-lg">AI Song from your lyrics</div>
      </div>
      <div className="text-[#A1A1AA] text-xs mb-4 leading-relaxed">
        Write your lyrics, pick a genre and a voice style. The finished track lands on your feed ready to drop into the decks.
      </div>
      <KeyGate service={service}>
        <textarea
          value={lyrics}
          onChange={e => setLyrics(e.target.value)}
          placeholder="Verse 1 / Chorus / Verse 2 …"
          rows={6}
          className="input-default mb-3 !text-sm resize-y"
          data-testid="songgen-lyrics"
        />
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-[#A1A1AA] font-bold mb-1">Genre</label>
            <select value={genre} onChange={e => setGenre(e.target.value)} className="input-default !text-sm" data-testid="songgen-genre">
              {['afrobeats', 'house', 'drill', 'grime', 'trap', 'r&b', 'pop', 'garage', 'techno', 'reggae'].map(g => <option key={g} value={g}>{g}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-[#A1A1AA] font-bold mb-1">Voice</label>
            <select value={voice} onChange={e => setVoice(e.target.value)} className="input-default !text-sm" data-testid="songgen-voice">
              <option value="sung">Sung</option>
              <option value="rap">Rap</option>
              <option value="instrumental">Instrumental</option>
            </select>
          </div>
        </div>
        <button
          onClick={submit}
          disabled={busy || !lyrics.trim() || !service.enabled}
          className="btn-pill btn-primary w-full flex items-center justify-center gap-2 text-sm disabled:opacity-40"
          data-testid="songgen-btn"
        >
          <Sparkles size={14} /> {busy ? 'Queuing…' : 'Generate song'}
        </button>
        {job && (
          <div className="mt-4 p-3 rounded-xl border border-[#FFB000]/30 bg-[#FFB000]/5" data-testid="songgen-job">
            <div className="text-[10px] uppercase tracking-widest text-[#FFB000] font-bold flex items-center gap-1">
              {job.status === 'ready' ? <><CheckCircle2 size={10} /> Ready</> : <><RefreshCw size={10} className="animate-spin" /> {job.status}</>}
            </div>
            {job.status === 'ready' && job.audio_url ? (
              <>
                <audio src={job.audio_url} controls className="w-full mt-2" data-testid="songgen-audio" />
                <button
                  onClick={() => navigate('/upload?ai=' + encodeURIComponent(job.audio_url))}
                  className="mt-2 btn-pill btn-outline w-full text-xs flex items-center justify-center gap-2"
                  data-testid="songgen-import"
                >
                  <UploadIcon size={12} /> Post this to my feed
                </button>
              </>
            ) : (
              <div className="text-[#A1A1AA] text-xs mt-1">Suno takes 1–2 minutes. You can leave this tab and come back.</div>
            )}
          </div>
        )}
      </KeyGate>
    </div>
  );
};

/**
 * StemSplitTile — accepts a storage path (from a track already uploaded) and
 * requests a Demucs split. Renders download links per stem once ready.
 */
const StemSplitTile = ({ service }) => {
  const [path, setPath] = useState('');
  const [job, setJob] = useState(null);

  const submit = async () => {
    if (!path.trim()) { toast.error('Paste an audio path from one of your posts'); return; }
    try {
      const { data } = await api.post('/ai/stems', { audio_path: path.trim() });
      setJob(data);
      toast.success('Stem split queued');
    } catch (err) {
      toast.error(err.response?.data?.detail?.message || 'Could not queue');
    }
  };

  useEffect(() => {
    if (!job || job.status === 'ready') return;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/ai/stems/${job.job_id}`);
        setJob(data);
        if (data.status === 'ready') clearInterval(t);
      } catch {}
    }, 10000);
    return () => clearInterval(t);
  }, [job]);

  return (
    <div className="post-card p-5" data-testid="stem-tile">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-full bg-[#FFB000]/10 flex items-center justify-center"><Scissors size={14} className="text-[#FFB000]" /></div>
        <div className="text-white font-heading font-black text-lg">Stem Split</div>
      </div>
      <div className="text-[#A1A1AA] text-xs mb-4 leading-relaxed">
        Split any track into vocals · drums · bass · other. Load each stem onto a different deck for pro remixing.
      </div>
      <KeyGate service={service}>
        <input value={path} onChange={e => setPath(e.target.value)} placeholder="rawmixes/audio/user_.../track.mp3" className="input-default mb-3 !text-xs" data-testid="stem-path" />
        <button onClick={submit} disabled={!service.enabled} className="btn-pill btn-primary w-full text-sm disabled:opacity-40" data-testid="stem-btn">Split into stems</button>
        {job && (
          <div className="mt-4" data-testid="stem-job">
            <div className="text-[10px] uppercase tracking-widest text-[#FFB000] font-bold">{job.status}</div>
            {job.status === 'ready' && job.stems && (
              <div className="mt-2 space-y-1">
                {Object.entries(job.stems).map(([name, url]) => (
                  <a key={name} href={url} className="block text-xs text-[#FFB000] hover:underline" download>
                    ↓ {name}.wav
                  </a>
                ))}
              </div>
            )}
          </div>
        )}
      </KeyGate>
    </div>
  );
};

export default function AIStudio() {
  const [status, setStatus] = useState(null);

  useEffect(() => {
    api.get('/ai/status').then(r => setStatus(r.data)).catch(() => setStatus({
      shazam: { enabled: false }, song_gen: { enabled: false }, stem_split: { enabled: false }
    }));
  }, []);

  return (
    <Layout>
      <div className="mb-6">
        <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#FFB000] mb-2">AI Studio</div>
        <h1 className="font-heading font-black text-white text-4xl md:text-5xl tracking-tighter">
          The bits nobody else has.
        </h1>
        <p className="text-[#A1A1AA] text-sm mt-2 max-w-xl">
          Identify what's playing, generate a track from your own lyrics, and split any song into stems.
          Each tile lights up as soon as its API key is in place.
        </p>
      </div>

      {!status && (
        <div className="post-card p-8 text-center text-[#A1A1AA] text-sm">Loading…</div>
      )}

      {status && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <ShazamTile service={status.shazam} />
          <SongGenTile service={status.song_gen} />
          <StemSplitTile service={status.stem_split} />
        </div>
      )}
    </Layout>
  );
}
